'use client'

import React, { useRef, useCallback, useEffect, useState } from 'react'
import { Box, TextField, IconButton, Tooltip } from '@mui/material'
import { Sun, Moon, Download } from 'lucide-react'
import type { TableViewProps, CellCoord } from '../types'
import { STYLES_CONFIG, COLUMN_CONFIG } from '../config'
import TableHeader from './subcomponents/TableHeader'
import TableBody from './subcomponents/TableBody'
import Pagination from './subcomponents/Pagination'
import LoadingOverlay from './subcomponents/LoadingOverlay'
import NoResultsOverlay from './subcomponents/NoResultsOverlay'
import ContextualMenu from './subcomponents/ContextualMenu'
import useCellSelection from './hooks/useCellSelection'
import useColumnResize from './hooks/useColumnResize'
import useInlineCellEdit from './hooks/useInlineCellEdit'
import useClipboardCopy from './hooks/useClipboardCopy'
import useTableViewContextMenu from './hooks/useTableViewContextMenu'
import { getCellsInfo } from './logic/domUtils'
import {
  selectColumnRange,
  selectRowRange,
  selectEntireRow,
  selectEntireColumn,
} from './logic/selectionLogic'
import {
  initDragListeners,
  handleHeaderMouseDown,
  handleHeaderTouchStart,
  handleRowIndexMouseDown,
  handleRowIndexTouchStart,
} from './logic/dragLogic'

export default function TableView({
  table,
  data,
  columnsDef,
  columnWidths,
  setColumnWidth,
  rowHeight,
  isDarkMode,
  toggleTheme,
  enableCellSelection,
  enableCellEditing,
  enableColumnFilters,
  enableExport,
  handleExportCSV,
  handleExportExcel,
  loadingText,
  noResultsText,
  globalFilter,
  setGlobalFilter,
  onHideColumns,
  onHideRows,
}: TableViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [highlightedRowIndex, setHighlightedRowIndex] = useState<number | null>(null)
  const [isDraggingColumns, setIsDraggingColumns] = useState(false)
  const [isDraggingRows, setIsDraggingRows] = useState(false)
  const [startColIndex, setStartColIndex] = useState<number | null>(null)
  const [startRowIndex, setStartRowIndex] = useState<number | null>(null)

  const dragStateRef = useRef({
    isDraggingCols: false,
    isDraggingRows: false,
    startColIndex: null as number | null,
    startRowIndex: null as number | null,
  })

  const rows = table.getRowModel().rows
  const visibleCols = table.getVisibleFlatColumns()

  const getCellsInfoCallback = useCallback(
    () => getCellsInfo(containerRef, table),
    [table]
  )

  const {
    selectedCells,
    setSelectedCells,
    handleKeyDownArrowSelection,
    edgeState,
    edgeIntensity,
    isSelecting,
  } = useCellSelection(
    containerRef,
    getCellsInfoCallback,
    data,
    columnsDef,
    table,
    { onSelectionChange: undefined }
  )

  const { handleMouseDownResize } = useColumnResize({
    columnWidths,
    setColumnWidth,
    originalColumnsDef: columnsDef,
  })

  const {
    editingCell,
    editingValue,
    isEditingCell,
    handleDoubleClick,
    handleChange,
    handleKeyDown: handleEditKeyDown,
    handleBlur,
    setInputRef,
  } = useInlineCellEdit({
    singleClickEdit: false,
    onNavigateNext: undefined,
  })

  const { copiedCells, setCopiedCells } = useClipboardCopy(
    containerRef,
    selectedCells,
    data,
    columnsDef
  )

  const doCopyCells = useCallback(async (cells: CellCoord[]) => {
    if (!cells.length) return
    const dataById = new Map(data.map(d => [String(d.id), d]))
    const rowsMap = new Map<string, string[]>()
    cells.forEach(cell => {
      const rowData = dataById.get(cell.id)
      if (!rowData) return
      const arr = rowsMap.get(cell.id) || []
      arr.push(String(rowData[cell.colField] ?? ''))
      rowsMap.set(cell.id, arr)
    })
    const lines: string[] = []
    for (const [, rowCells] of rowsMap) lines.push(rowCells.join('\t'))
    await navigator.clipboard.writeText(lines.join('\n'))
    setCopiedCells([...cells])
  }, [data, setCopiedCells])

  const {
    contextMenu,
    clickedHeaderIndex,
    clickedRowIndex,
    handleContextMenu,
    handleCloseContextMenu,
    handleCopyFromMenu,
    handleHideColumn,
    handleHideRow,
  } = useTableViewContextMenu({
    selectedCells,
    doCopyCells,
    onHideColumns,
    onHideRows,
    table,
    rows: data,
  })

  const selectColumnRangeFn = useCallback((start: number, end: number) => {
    const sel = selectColumnRange({ rows, visibleCols, start, end })
    setSelectedCells(sel)
  }, [rows, visibleCols, setSelectedCells])

  const selectRowRangeFn = useCallback((start: number, end: number) => {
    const sel = selectRowRange({ rows, visibleCols, start, end })
    setSelectedCells(sel)
  }, [rows, visibleCols, setSelectedCells])

  const selectEntireRowFn = useCallback((rowIndex: number, rowId: string) => {
    const sel = selectEntireRow({ rowIndex, rowId, visibleCols })
    setSelectedCells(sel)
  }, [visibleCols, setSelectedCells])

  const onHeaderMouseDown = useCallback((evt: MouseEvent, colIndex: number, colId: string) => {
    handleHeaderMouseDown(evt, colIndex, colId, dragStateRef, setIsDraggingColumns, setStartColIndex)
    const sel = selectEntireColumn({ rows, colId })
    setSelectedCells(sel)
  }, [rows, setSelectedCells])

  const onHeaderTouchStart = useCallback((evt: TouchEvent, colIndex: number, colId: string) => {
    handleHeaderTouchStart(evt, colIndex, colId, dragStateRef, setIsDraggingColumns, setStartColIndex)
    const sel = selectEntireColumn({ rows, colId })
    setSelectedCells(sel)
  }, [rows, setSelectedCells])

  const onRowIndexMouseDown = useCallback((evt: MouseEvent, rowIndex: number, rowId: string) => {
    handleRowIndexMouseDown(evt, rowIndex, rowId, selectEntireRowFn, dragStateRef, setIsDraggingRows, setStartRowIndex)
  }, [selectEntireRowFn])

  const onRowIndexTouchStart = useCallback((evt: TouchEvent, rowIndex: number, rowId: string) => {
    handleRowIndexTouchStart(evt, rowIndex, rowId, selectEntireRowFn, dragStateRef, setIsDraggingRows, setStartRowIndex)
  }, [selectEntireRowFn])

  useEffect(() => {
    return initDragListeners({
      containerRef,
      setIsDraggingColumns,
      setIsDraggingRows,
      setStartColIndex,
      setStartRowIndex,
      dragStateRef,
      rows,
      selectColumnRangeFn,
      selectRowRangeFn,
    })
  }, [rows, selectColumnRangeFn, selectRowRangeFn])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.addEventListener('contextmenu', handleContextMenu)
    return () => el.removeEventListener('contextmenu', handleContextMenu)
  }, [handleContextMenu])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        if (!editingCell) {
          handleKeyDownArrowSelection(e)
        }
      }
    }
    const el = containerRef.current
    if (!el) return
    el.addEventListener('keydown', handleKeyDown)
    return () => el.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDownArrowSelection, editingCell])

  const handleCellClick = useCallback((rowIndex: number) => {
    setHighlightedRowIndex(rowIndex)
  }, [])

  const isLoading = false
  const hasData = rows.length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderBottom: '1px solid var(--color-divider)',
        bgcolor: 'var(--color-bg-paper)',
      }}>
        <TextField
          size="small"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={e => setGlobalFilter(e.target.value)}
          sx={{ flex: 1, maxWidth: 300 }}
        />

        <Box sx={{ flex: 1 }} />

        {enableExport && (
          <Tooltip title="Exportar CSV">
            <IconButton onClick={handleExportCSV} size="small">
              <Download size={18} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={isDarkMode ? 'Modo claro' : 'Modo oscuro'}>
          <IconButton onClick={toggleTheme} size="small">
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        ref={containerRef}
        className="tv-scroll"
        tabIndex={0}
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          outline: 'none',
          '&::-webkit-scrollbar': { width: STYLES_CONFIG.scrollbarWidth, height: STYLES_CONFIG.scrollbarWidth },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--color-divider)', borderRadius: 4 },
        }}
      >
        {isLoading && <LoadingOverlay loadingText={loadingText} />}
        {!isLoading && !hasData && <NoResultsOverlay noResultsText={noResultsText} />}

        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}>
          <TableHeader
            table={table}
            columnWidths={columnWidths}
            originalColumnsDef={columnsDef}
            handleMouseDownResize={handleMouseDownResize}
            onHeaderMouseDown={onHeaderMouseDown}
            onHeaderTouchStart={onHeaderTouchStart}
            isDarkMode={isDarkMode}
            showColumnFilters={enableColumnFilters}
          />
          <TableBody
            rows={rows}
            rowHeight={rowHeight}
            isEditingCell={isEditingCell}
            editingValue={editingValue}
            handleDoubleClick={handleDoubleClick}
            handleChange={handleChange}
            handleEditKeyDown={handleEditKeyDown}
            handleBlur={handleBlur}
            setInputRef={setInputRef}
            selectedCells={selectedCells}
            copiedCells={copiedCells}
            handleCellClick={handleCellClick}
            onRowIndexMouseDown={onRowIndexMouseDown}
            onRowIndexTouchStart={onRowIndexTouchStart}
            highlightedRowIndex={highlightedRowIndex}
            isDarkMode={isDarkMode}
          />
        </table>

      </Box>

      <Pagination table={table} />

      <ContextualMenu
        contextMenu={contextMenu}
        handleCloseContextMenu={handleCloseContextMenu}
        handleCopyFromMenu={handleCopyFromMenu}
        clickedHeaderIndex={clickedHeaderIndex}
        onHideColumns={onHideColumns}
        handleHideColumn={handleHideColumn}
        clickedRowIndex={clickedRowIndex}
        onHideRows={onHideRows}
        handleHideRow={handleHideRow}
      />
    </Box>
  )
}

export type { CellCoord }
